import { env } from './env';

/**
 * Centralized observability. Two concerns:
 *  - track(): admin-action telemetry, shaped to match the backend audit record
 *    (action / target / actor / result) so events correlate by request id.
 *  - reportError(): Sentry-compatible error reporting with PII scrubbing.
 *
 * Neither ships a heavy SDK; both expose a hook (`installErrorReporter`) so a
 * real Sentry client can be wired at the edge without touching call sites.
 */

export interface TrackEvent {
  action: string;
  target?: string;
  result?: 'success' | 'failure' | 'attempt';
  requestId?: string;
  metadata?: Record<string, unknown>;
}

type Sink = (event: TrackEvent) => void;

// Default sink: structured console line in dev, no-op shape in prod (a real
// collector is installed via setTrackSink at app boot).
let sink: Sink = (event) => {
  if (env.isDev) {
    console.debug('[track]', event.action, event);
  }
};

export function setTrackSink(fn: Sink): void {
  sink = fn;
}

export function track(event: TrackEvent): void {
  try {
    sink(event);
  } catch {
    /* telemetry must never break the app */
  }
}

// ---- Error reporting (Sentry-compatible interface) ------------------------

export interface ErrorReporter {
  captureException(error: unknown, context?: Record<string, unknown>): void;
}

const PII_KEYS = /(email|password|token|secret|authorization|cookie|ssn|phone|name)/i;

/** Recursively redact PII-looking keys before anything leaves the browser. */
export function scrubPii(value: unknown, depth = 0): unknown {
  if (depth > 6 || value == null) return value;
  if (Array.isArray(value)) return value.map((v) => scrubPii(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = PII_KEYS.test(k) ? '[redacted]' : scrubPii(v, depth + 1);
    }
    return out;
  }
  return value;
}

let reporter: ErrorReporter | null = null;

export function installErrorReporter(impl: ErrorReporter): void {
  reporter = impl;
}

export function reportError(error: unknown, context: Record<string, unknown> = {}): void {
  const scrubbed = scrubPii(context) as Record<string, unknown>;
  if (reporter) {
    reporter.captureException(error, scrubbed);
  } else if (env.isDev) {
    console.error('[reportError]', error, scrubbed);
  }
  // If a real DSN is configured but no reporter installed, that's a wiring gap —
  // surface it once in dev.
  if (env.sentryDsn && !reporter && env.isDev) {
    console.warn('[observability] SENTRY_DSN set but no reporter installed');
  }
}
